/**
 * Resources Section Component
 * Displays links to videos and articles about OKR services
 */

import React from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { Button } from './Button';
import { PlayCircle, FileText, ArrowRight, ExternalLink } from 'lucide-react';

interface Video {
  title: string;
  description: string;
  thumbnailUrl?: string;
  videoUrl: string;
  duration: string;
}

interface Article {
  title: string;
  description: string;
  articleUrl: string;
  readTime: string;
  category: string;
}

const videos: Video[] = [
  {
    title: 'Introduction to OKRs',
    description: 'Learn the fundamentals of Objectives and Key Results and why they matter for organizational alignment.',
    videoUrl: '#',
    duration: '8 min'
  },
  {
    title: 'Writing Effective Key Results',
    description: 'Master the art of crafting measurable, outcome-driven key results that drive real progress.',
    videoUrl: '#',
    duration: '12 min'
  },
  {
    title: 'OKR Review Cadences',
    description: 'Discover how to run effective weekly check-ins and quarterly reviews that create accountability.',
    videoUrl: '#',
    duration: '10 min'
  }
];

const articles: Article[] = [
  {
    title: 'Why OKRs Fail: The 5 Most Common Mistakes',
    description: 'Understanding the pitfalls that derail OKR implementations and how to avoid them.',
    articleUrl: '#',
    readTime: '6 min read',
    category: 'Strategy'
  },
  {
    title: 'OKRs vs KPIs: Understanding the Difference',
    description: 'A practical guide to using OKRs and KPIs together for better performance management.',
    articleUrl: '#',
    readTime: '5 min read',
    category: 'Framework'
  },
  {
    title: 'Building an OKR Culture in Your Organization',
    description: 'How to create buy-in and establish OKRs as a management discipline, not just another process.',
    articleUrl: '#',
    readTime: '8 min read',
    category: 'Culture'
  },
  {
    title: 'Aligning Team OKRs with Company Strategy',
    description: 'Practical techniques for cascading objectives while maintaining team autonomy.',
    articleUrl: '#',
    readTime: '7 min read',
    category: 'Alignment'
  }
];

export const ResourcesSection: React.FC = () => {
  return (
    <Section id="resources" background="cream" padding="xl">
      <SectionHeader
        subtitle="Learn & Grow"
        title="OKR Resources"
        description="Explore our collection of videos and articles to deepen your understanding of OKRs and improve your implementation."
        align="center"
      />

      {/* Videos Section */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <PlayCircle className="w-5 h-5 text-primary-600" />
          </div>
          <h3 className="font-display text-2xl text-ink">Videos</h3>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <a
              key={index}
              href={video.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-xl overflow-hidden shadow-soft-sm hover:shadow-soft-md transition-all duration-300 border border-stone-200 hover:border-primary-300"
            >
              {/* Video Thumbnail Placeholder */}
              <div className="aspect-video bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center relative">
                <PlayCircle className="w-16 h-16 text-white/90 group-hover:scale-110 transition-transform duration-300" />
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {video.duration}
                </div>
              </div>

              {/* Video Info */}
              <div className="p-5">
                <h4 className="font-semibold text-ink mb-2 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h4>
                <p className="text-sm text-ink-light line-clamp-2">
                  {video.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Articles Section */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>
          <h3 className="font-display text-2xl text-ink">Articles</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {articles.map((article, index) => (
            <a
              key={index}
              href={article.articleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-xl p-6 shadow-soft-sm hover:shadow-soft-md transition-all duration-300 border border-stone-200 hover:border-primary-300 flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="inline-flex items-center px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <ExternalLink className="w-4 h-4 text-stone-400 group-hover:text-primary-500 transition-colors" />
              </div>

              <h4 className="font-semibold text-ink mb-2 group-hover:text-primary-600 transition-colors">
                {article.title}
              </h4>

              <p className="text-sm text-ink-light mb-4 flex-grow">
                {article.description}
              </p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-ink-muted">{article.readTime}</span>
                <span className="text-sm font-medium text-primary-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read more
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-16 text-center bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl py-12 px-8">
        <h3 className="font-display text-2xl text-ink mb-3">
          Ready to Build a High-Performance Culture?
        </h3>
        <p className="text-ink-muted mb-8 max-w-xl mx-auto">
          Let's discuss how OKR Hub can help your organization achieve clarity, alignment, and execution excellence.
        </p>
        <Button
          size="lg"
          variant="primary"
          as="a"
          href="#contact"
          className="text-lg px-10 py-4"
        >
          Schedule a Discovery Call
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </Section>
  );
};
